import React, { useEffect, useRef, useMemo } from 'react'
import { observer } from 'mobx-react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import "./Layout.css"
import { ErrorBoundary } from 'react-error-boundary'
import { FallbackErrorComponent } from 'src/components/errors/FallbackErrorComponent'
import WebSocketService from 'src/classes/WebSocketService'
import { applicationStore } from 'src/store/ApplicationStore'
import LanguageSwitcher from 'src/components/ui/LanguageSwitcher'

// Pages where language switcher should be hidden
const PAGES_WITHOUT_LANGUAGE_SWITCHER = [
    '/error-page',
    '/error-access-camera',
    '/error-no-camera',
    '/error-bank'
];
const MainLayout = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const outerScrollRef = useRef(null);

    // Check if current page should hide language switcher
    const shouldShowLanguageSwitcher = useMemo(() => {
        return !PAGES_WITHOUT_LANGUAGE_SWITCHER.includes(location.pathname);
    }, [location.pathname]);
    useEffect(() => {
        if (applicationStore?.access_token) {
            const webSocketService = WebSocketService.getInstance();

            webSocketService.initialize(
                'wss://' + window.location.host + '/websocket',
                applicationStore.access_token,
                applicationStore,
                navigate
            );
        }
        return () => {
            const webSocketService = WebSocketService.getInstance();
            webSocketService.close();
        };
    }, []);

    useEffect(() => {
        const canScroll = (el, scrollingDown) => !!el && (scrollingDown
            ? el.scrollTop + el.clientHeight < el.scrollHeight - 1
            : el.scrollTop > 0);

        const isScrollableY = (el) => {
            if (!el || el.nodeType !== 1) return false;
            const overflowY = window.getComputedStyle(el).overflowY;
            return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1;
        };

        const findScrollableAncestor = (start, boundary) => {
            let node = start;
            while (node && node !== boundary && node !== document.body) {
                if (isScrollableY(node)) return node;
                node = node.parentElement;
            }
            return null;
        };

        const findScrollableDescendant = (root) => {
            if (!root) return null;
            const stack = [root];
            while (stack.length) {
                const node = stack.pop();
                if (isScrollableY(node)) return node;
                for (const child of node.children) stack.push(child);
            }
            return null;
        };

        const handleWheel = (event) => {
            const outer = outerScrollRef.current;
            if (!outer) return;

            const scrollingDown = event.deltaY > 0;
            const hovered = findScrollableAncestor(event.target, outer);

            if (hovered) {
                if (canScroll(hovered, scrollingDown)) return;
                if (canScroll(outer, scrollingDown)) {
                    outer.scrollTop += event.deltaY;
                    event.preventDefault();
                }
                return;
            }

            if (canScroll(outer, scrollingDown)) return;
            const content = findScrollableDescendant(outer);
            if (canScroll(content, scrollingDown)) {
                content.scrollTop += event.deltaY;
                event.preventDefault();
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, []);
    const logError = (error, info) => {
    };

    return (
        <div ref={outerScrollRef} className="flex min-h-[100dvh] items-start justify-center overflow-y-auto font-RocGroteskRegular md:items-center">
            <div className="flex w-full flex-col items-start justify-center md:items-center">
                <div
                    className={`flow-app-shell ${applicationStore.clientBranding ? 'flow-themed' : ''} relative mx-auto mt-0 flex h-[100dvh] min-h-[100dvh] w-full max-w-[393px] flex-col overflow-hidden rounded-none border-0 shadow-none scrollbar-none md:mt-4 md:h-[min(852px,100svh)] md:min-h-[min(852px,100svh)] md:rounded-[var(--flow-radius-xl)] md:border md:border-black/[0.08] md:shadow-[var(--flow-shadow-shell)]`}
                    style={{
                        backgroundColor:
                            applicationStore.clientBranding?.layoutShellStyle?.backgroundColor ??
                            '#ffffff',
                        ...(applicationStore.clientBranding?.layoutShellStyle?.color != null
                            ? { color: applicationStore.clientBranding.layoutShellStyle.color }
                            : {}),
                        ...(applicationStore.clientBranding?.layoutShellStyle?.borderColor != null
                            ? { borderColor: applicationStore.clientBranding.layoutShellStyle.borderColor }
                            : {}),
                        ...(applicationStore.clientBranding?.layoutShellStyle?.fontFamily != null
                            ? { fontFamily: applicationStore.clientBranding.layoutShellStyle.fontFamily }
                            : {}),
                        ...(applicationStore.clientBranding?.cssVariables ?? {}),
                    }}
                >
                    {shouldShowLanguageSwitcher && (
                        <div className="absolute right-[max(0.75rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] z-20">
                            <LanguageSwitcher />
                        </div>
                    )}
                    <ErrorBoundary
                        fallback={FallbackErrorComponent}
                        onReset={(details) => {
                        }}
                        onError={logError}
                    >
                        <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col h-full max-h-full w-full">
                            <Outlet />
                        </div>
                    </ErrorBoundary>
                </div>
            </div>
        </div>
    );
});

export default MainLayout;